// Inline run-detail view for the workflow builder's runs panel — replaces
// the older summary-only `<RunPanel>` widget. Shows the same expandable
// per-step trace that the standalone `/triage/runs/:id` page renders,
// adapted to the narrower side-panel width. Clicking a run in the panel
// list now opens this inline view without navigating away from the
// builder. (DEV-16160.)
import type { Run } from '@cortex/types';
import { Link } from '@tanstack/react-router';
import { type CSSProperties } from 'react';

import { routes, terminalRunStatuses, type RunStatusKey } from '../../../lib/constants';
import { formatDateTime, formatDuration } from '../../../lib/format';
import { StatusBadge } from '../../common/StatusBadge';
import { RunExecutionTrace } from '../../runs/RunExecutionTrace';
import { useWorkflowEditor } from '../WorkflowEditorContext';

const container: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-3)',
  padding: 'var(--sp-3)',
};

const headerCard: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
  padding: 'var(--sp-3)',
  background: 'var(--surface-2)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
};

const titleRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--sp-2)',
};

const metaRow: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--sp-3)',
  fontSize: 'var(--fs-xs)',
  color: 'var(--text-secondary)',
};

const footerRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: 'var(--fs-xs)',
};

/**
 * Read `live` off the editor context for the run summary (useLiveRun is
 * already polling for the canvas's per-node state, so we piggyback rather
 * than firing a second `/v1/runs/:id` query). The per-step trace fetches
 * `/v1/runs/:id/node-executions` independently via `RunExecutionTrace` so
 * the same component the triage board uses can render here unchanged.
 */
export function InlineRunDetail({ runId, onClose }: { runId: string; onClose: () => void }) {
  const { live } = useWorkflowEditor();
  const run = live.run;
  const isActive = run ? !terminalRunStatuses.has(run.status as RunStatusKey) : true;
  // SSE subscription lives one level up in `WorkflowEditorContext` (so
  // the canvas's `is-running` / `is-next` outlines update live even when
  // this panel is closed). The bubble thread here piggybacks on those
  // same cache invalidations. (DEV-16156 moved this from here.)

  return (
    <div style={container}>
      <RunSummaryHeader runId={runId} run={run} onClose={onClose} />
      <RunExecutionTrace runId={runId} isActive={isActive} />
      <div style={footerRow}>
        <Link to={routes.runDetail(runId)} style={{ color: 'var(--text-secondary)' }}>
          Open full run page →
        </Link>
      </div>
      {live.error ? (
        <div className="alert alert--danger" style={{ fontSize: 12 }}>
          Live polling failed: {live.error.message}
        </div>
      ) : null}
    </div>
  );
}

function RunSummaryHeader({
  runId,
  run,
  onClose,
}: {
  runId: string;
  run: Run | null;
  onClose: () => void;
}) {
  return (
    <div style={headerCard}>
      <div style={titleRow}>
        <code className="t-mono" style={{ fontSize: 'var(--fs-sm)' }}>
          RUN · {runId.slice(0, 16)}
        </code>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          {run ? <StatusBadge status={run.status} /> : null}
          <button
            type="button"
            className="btn btn--ghost btn--xs"
            onClick={onClose}
            aria-label="Close run detail"
          >
            ✕
          </button>
        </div>
      </div>
      {run ? (
        <div style={metaRow}>
          <span>
            <span className="t-mono-label">Trigger</span> {run.trigger}
          </span>
          <span>
            <span className="t-mono-label">Started</span> {formatDateTime(run.startedAt)}
          </span>
          <span>
            <span className="t-mono-label">Duration</span>{' '}
            {formatDuration(run.startedAt, run.finishedAt)}
          </span>
        </div>
      ) : (
        <span className="t-xs t-secondary">Starting…</span>
      )}
      {run?.error ? (
        <div className="alert alert--danger" style={{ fontSize: 12, marginTop: 'var(--sp-2)' }}>
          {typeof run.error === 'string' ? run.error : JSON.stringify(run.error, null, 2)}
        </div>
      ) : null}
    </div>
  );
}
